import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import { logJsError, logFetchError } from './utils/errorLogger'
import { idbGet, idbDelete } from './shared/lib/idb'

import Login from './pages/Login'
import ChangePassword from './pages/ChangePassword'
import Master from './pages/Master'
import Report from './pages/Report'
import Ros from './pages/Ros'
import Workshop from './pages/Workshop'
import Admin from './pages/Admin'
import Mechanic from './pages/Mechanic'
import OrderPage from './pages/OrderPage'
import Layout from './shared/ui/Layout'
const Employees = lazy(() => import('./pages/Employees'))
const Salons = lazy(() => import('./pages/Salons'))
const ServiceDirector = lazy(() => import('./pages/ServiceDirector'))

if (typeof window !== 'undefined') {
  window.onerror = (message, source, lineno, colno, error) => {
    logJsError(String(message), error?.stack, window.location.href)
    return false
  }
  window.addEventListener('unhandledrejection', (e) => {
    logJsError(String(e.reason?.message || e.reason), e.reason?.stack, window.location.href)
  })

  const originalFetch = window.fetch
  window.fetch = async (...args) => {
    try {
      const res = await originalFetch(...args)
      if (res.status === 401) {
        localStorage.removeItem('token')
        window.location.href = '/login'
        return res
      }
      if (res.status === 403) {
        const failure = await res.clone().json().catch(() => null)
        if (failure?.code === 'AUTH_USER_INACTIVE') {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          window.location.href = '/login'
          return res
        }
      }
      if (!res.ok && res.status >= 400) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url
        logFetchError(`HTTP ${res.status} ${res.statusText}`, url)
      }
      return res
    } catch (err) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url
      logJsError(err.message, err.stack, url)
      throw err
    }
  }
}

function RequireAuth({ children, allowPasswordChange = false }) {
  const { user, loading } = useAuth()
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F2F4F7' }}>
      <p style={{ color: '#6B7280' }}>Загрузка...</p>
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  if (user.must_change_password && !allowPasswordChange) return <Navigate to="/change-password" replace />
  return children
}

function RolePage() {
  const { user } = useAuth()
  if (user.role === 'master') return <Master />
  if (user.role === 'ros') return <Ros />
  if (user.role === 'master_workshop') return <Workshop />
  if (user.role === 'admin') return <Admin />
  if (user.role === 'mechanic') return <Mechanic />
  if (user.role === 'service_director') return <Suspense fallback={<p style={{padding:16}}>Загрузка обзора сети…</p>}><ServiceDirector /></Suspense>
  return <Navigate to="/login" replace />
}

function EmployeesPage() {
  const { user } = useAuth()
  if (user?.role === 'service_director') return <Navigate to="/salons" replace />
  return user?.role === 'ros' ? <Suspense fallback={<p style={{ padding: '16px', fontSize: '13px', color: '#6B7280' }}>Загрузка сотрудников…</p>}><Employees /></Suspense> : <Navigate to="/" replace />
}

function DirectorPage({ employees=false }) {
  const { user } = useAuth()
  const { salonId } = useParams()
  return user?.role === 'service_director' ? <Suspense fallback={<p style={{padding:16}}>Загрузка…</p>}>{employees ? <Employees key={salonId} /> : <Salons />}</Suspense> : <Navigate to="/" replace />
}

function AppContent() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onMessage = (event) => {
      if (event.data?.type === 'notification-click') {
        const { url, notificationId } = event.data
        if (url) window.location.href = url
        if (notificationId) {
          const token = localStorage.getItem('token')
          if (token) {
            fetch(`/api/notifications/${notificationId}/read`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => { })
          }
        }
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    idbGet('pendingNotificationId').then(notificationId => {
      if (!notificationId) return
      fetch(`/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => { })
      idbDelete('pendingNotificationId')
    }).catch(() => { })
  }, [])

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/change-password" element={<RequireAuth allowPasswordChange><ChangePassword /></RequireAuth>} />
      <Route path="/report/:token" element={<Report />} />
      <Route path="/report/preview/:token" element={<Report />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route path="/" element={<RolePage />} />
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/salons" element={<DirectorPage />} />
        <Route path="/salons/:salonId/employees" element={<DirectorPage employees />} />
        <Route path="/orders/:id" element={<OrderPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  )
}
